import React, { useState, useCallback } from 'react';
import { Button, Dropdown, message } from 'antd';
import { BugOutlined, CheckCircleOutlined, AlertOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import BugReportOverlay from './BugReportOverlay';
import VerifyModal from './VerifyModal';
import { bugReportApi } from '../../services/bugReportApi';
import { devApi } from '../../services/devApi';
import { erpHomeApi } from '../../services/erpHomeApi';
import type { Annotation } from './types';

const routeToFormId: Record<string, string> = {
  // Sales Contract (SC)
  '/sc': 'pwc202f1',
  // Product Grade
  '/product-grade': 'pw1118f1',
  // Complaint Description
  '/complaint-desc': 'pwn102f1',
  // Office Supplies
  '/office-supplies': 'pwb151f1',
  // Subcontracting Category
  '/d103': 'pwd103f1',
  // Exchange Rate
  '/exchange-rate': 'fin_exchangerate',
  // Inventory Movement Category
  '/inv-move-cat': 'pwm101f1',
  // Company Master
  '/company': 'pw1104f1',
  // Inventory Reason
  '/inv-reason': 'pwm104f1',
  // Department Setup
  '/dept': 'pw1106f1',
  // Product Master
  '/product': 'pw1113f1',
  // Trade Terms
  '/trade-terms': 'pw1117f1',
  // Quality Requirements
  '/quality-req': 'pw111hf1',
  // Test Item Setup
  '/test-item': 'pw111af1',
  // Material Listing Report
  '/material-report': 'pw1311f1',
  // Material Master
  '/material': 'pw1111f1',
  // Payment Terms
  '/payment-terms': 'pw1116f1',
  // Warehouse Return Statistics
  '/warehouse-return-stats': 'pwd318f1',
  // Order Follow-up Report
  '/c322': 'pwc322f1',
  // Sales Return Writeoff
  '/c5m5': 'pwc5m5f1',
  // S/C Approval
  '/c502': 'pwc502f1',
  // Warehouse Return Out Approval
  '/d506': 'pwd506f1',
  // Delivery Order Writeoff
  '/c5m4': 'pwc5m4f1',
  // S/C Writeoff Approval
  '/c5m2': 'pwc5m2f1',
  // Delivery Order
  '/c204': 'pwc204f1',
  // Subcontract Receipt Statistics
  '/d319': 'pwd319f1',
  // Currency Listing Report
  '/currency-report': 'pw1308f1',
  // Department Listing Report
  '/dept-report': 'pw1306f1',
  // NHI Parameters
  '/nhi-params': 'pw9137f1',
  // Employee Group
  '/emp-group': 'pw910tf1',
  // Remark
  '/remark': 'pw110cf1',
  // Casual Employee Payroll
  '/casual-payroll': 'pw913df1',
  // Labor Insurance Parameters
  '/labor-ins': 'pw9136f1',
  // Complaint Cause
  '/complaint-cause': 'pwn101f1',
  // Document Setup
  '/doc-setup': 'pwl102f1',
  // Inventory Processing Method
  '/inv-process': 'pwm105f1',
  // Pension Parameters
  '/pension-params': 'pw9138f1',
  // Pack Customer
  '/pack-customer': 'pwp151f1',
  // Pay Item
  '/pay-item': 'pw9132f1',
  // Leave Type Setup
  '/leave-type': 'pw9108f1',
  // Inspection Method
  '/insp-method': 'pw111gf1',
  // Work Schedule
  '/work-schedule': 'pw910gf1',
  // VP Approval Priority
  '/vp-approval-priority': 'pw910jf1',
  // Vendor Evaluation
  '/vendor-eval': 'pw110hf1',
  // Job Grade Definition
  '/job-grade': 'pw9103f1',
  // Department Major Definition
  '/dept-major': 'pw910qf1',
  // R&D Subcontracting Order Reversal
  '/j5n5': 'pwj5n5f1',
  // New-product analysis approval reversal
  '/j5n8': 'pwj5n8f1',
  // PO Approval
  '/d501': 'pwd501f1',
  // Attendance Approval for VP
  '/p9513': 'pw9513f1',
  // Color Code Listing Report
  '/color-report': 'pw1314f1',
  // Sales Return Detail Report
  '/c330': 'pwc330f1',
  // Vendor Evaluation Approval
  '/p15mh': 'pw15mhf1',
  // Vendor Evaluation R&D Approval
  '/p15mj': 'pw15mjf1',
  // Attendance Status Query
  '/attendance-status': 'pw9410f1',
  // Warehouse Return Reversal
  '/d5m2': 'pwd5m2f1',
  '/pwd216': 'pwd216f1',
  '/pwd217': 'pwd217f1',
  '/d5n2': 'pwd5n2f1',
  '/d514': 'pwd514f1',
  '/d5n4': 'pwd5n4f1',
  '/d5m6': 'pwd5m6f1',
  // S/C Closeout Approval
  '/c516': 'pwc516f1',
  // Warehouse Return Out Reversal
  '/r5d6': 'pwr5d6f1',
  // Sales Return Writeoff V2
  '/c526': 'pwc526f1',
  // Receipt Approval
  '/d502': 'pwd502f1',
  // Announcement Setup
  '/announcement': 'pwt102f1',
  // Announcement Application
  '/t202': 'pwt202f1',
  // Complaint Record - Purchasing
  '/n202': 'pwn202f1',
  // Inventory Shipment Reversal
  '/r5m3': 'pwr5m3f1',
  // Announcement Approval Reversal
  '/t5m2': 'pwt5m2f1',
  // Announcement Query
  '/announcement-query': 'pwt402f1',
  // Normal Inventory Adjustment Approval
  '/m501': 'pwm501f1',
  // Inventory Adjustment
  '/m201': 'pwm201f1',
  // Board Number Modification
  '/m207': 'pwm207f1',
  // Meeting Record
  '/t206': 'pwt206f1',
  // Requisition Reversal
  '/r5d5': 'pwr5d5f1',
  // Actual Purchase Cost Calculation
  '/r211': 'pwr211f1',
  // Negative Inventory Report
  '/m310': 'pwm310f1',
  // Location Batch Inventory Report
  '/m303': 'pwm303f1',
  // Complaint Tracking Query
  '/n351': 'pwn351f1',
  // Consignment Delivery Reversal
  '/r5m8': 'pwr5m8f1',
  // Complaint Approval - Sales Manager
  '/n501': 'pwn501f1',
  '/n502': 'pwn502f1',
  '/n503': 'pwn503f1',
  // Inventory Delivery List Reversal
  '/m5m3': 'pwm5m3f1',
  // Inventory Delivery List Entry / Approval
  '/m203': 'pwm203f1',
  '/m503': 'pwm503f1',
  // Inspection Request
  '/inspection-request': 'pwp201f1',
  // Complaint Purchase Shipment Approval
  '/r5np': 'pwr5npf1',
  // Product Adjustment Reversal
  '/m5m1': 'pwm5m1f1',
  // Consignment Delivery Writeoff
  '/m5m8': 'pwm5m8f1',
  // Complaint Purchase Shipment Reversal
  '/n5p1': 'pwn5p1f1',
  // Receipt Reversal
  '/r5d2': 'pwr5d2f1',
  // Physical Test Approval
  '/p503': 'pwp503f1',
  // Physical Test Approval Reversal
  '/p5m3': 'pwp5m3f1',
  // Total Inventory Report
  '/m301': 'pwm301f1',
  // Order Batch Inventory Report
  '/m305': 'pwm305f1',
  // Complaint QA Approval Reversal
  '/n5m3': 'pwn5m3f1',
  // R&D Rack Number Modification
  '/m209': 'pwm209f1',
  // Sample Request Approval
  '/j501': 'pwj501f1',
  // R&D PO Approval
  '/j514': 'pwj514f1',
  // Grade Personnel Listing
  '/pw030a': 'pw030af1',
  // System Usage Log
  '/pw0308': 'pw0308f1',
  // Physical Test Report
  '/p203': 'pwp203f1',
  // R&D Purchase Order Reversal
  '/j5n4': 'pwj5n4f1',
  // Sample Delivery Reversal
  '/j5m3': 'pwj5m3f1',
  // R&D Warehouse Return Reversal
  '/j5m8': 'pwj5m8f1',
  // Sample Request Approval Reversal
  '/j5m1': 'pwj5m1f1',
  // Document Listing Report
  '/l303': 'pwl303f1',
  // Document Print/View
  '/l313': 'pwl313f1',
  // Label Printing
  '/j30c': 'pwj30cf1',
  // Inventory Category Maintenance
  '/m106': 'pwm106f1',
  // Packing Method Maintenance
  '/pw1115f1': 'pw1115f1',
  // New Employee Record
  '/pw910cf1': 'pw910cf1',
  // Change Password
  '/pw0113f1': 'pw0113f1',
  // R&D Subcontract Order Approval
  '/j515': 'pwj515f1',
  // Document Query
  '/l402': 'pwl402f1',
  // ISO Document Query
  '/l413': 'pwl413f1',
  // Chapter Content Maintenance
  '/l202': 'pwl202f1',
  // R&D Requisition
  '/j207': 'pwj207f1',
  // Sample Request
  '/j201': 'pwj201f1',
  // Sample Warehouse Receipt
  '/j202': 'pwj202f1',
  // Sample Receipt Approval
  '/j502': 'pwj502f1',
  // R&D Sample Warehouse Receipt
  '/j208': 'pwj208f1',
  // Sales Album Production
  '/j212': 'pwj212f1',
  // New Product Dev Analysis
  '/j219': 'pwj219f1',
  // R&D Purchase Order
  '/j214': 'pwj214f1',
  // Subcontract Receipt Reversal
  '/d5m4': 'pwd5m4f1',
  // Purchase Order Approval Reversal
  '/d5m1': 'pwd5m1f1',
  // Sample Receipt Reversal
  '/j5m2': 'pwj5m2f1',
  // Sample Delivery
  '/j203': 'pwj203f1',
  // Warehouse Location
  '/warehouse-location': 'pw1105f1',
  // Color Code Definition
  '/color-code': 'pw1114f1',
  // Business Card Personal Data
  '/business-card': 'pw1103f1',
  // Employee Master
  '/employee': 'pw9101f1',
  // Product Master (PRD)
  '/product-master': 'prd_product',
  // Customer Master
  '/customer': 'cus_customer',
  // Quotation Management
  '/pwc201f1': 'pwc201f1',
  // Leave Application
  '/pw9221f1': 'pw9221f1',
  // Bonus Distribution Record
  '/pw9236f1': 'pw9236f1',
  // Personnel Transfer
  '/pw9204f1': 'pw9204f1',
  // Daily Attendance Record
  '/pw9210f1': 'pw9210f1',
  // Sales Return
  '/pw2204f1': 'pw2204f1',
  // Batch 10: Complaint Requirement
  '/complaint-req': 'pwn103f1',
  // Batch 10: Defect Cause
  '/defect-cause': 'pwp111f1',
  // Batch 10: Processing Fee
  '/processing-fee': 'pw1112f1',
  // Batch 10: Bonus Definition
  '/bonus-def': 'pw9133f1',
  // Batch 10: Processing Quality
  '/proc-quality': 'pw111jf1',
  // Batch 10: Employee Shortcode
  '/emp-shortcode': 'pw111kf1',
  // Batch 10: Department Staffing
  '/dept-staffing': 'pw9106f1',
  // Batch 10: Proxy Setting
  '/proxy-setting': 'pw9105f1',
  // Batch 10: Job Title
  '/job-title': 'pw9102f1',
  // Batch 10: Holiday
  '/holiday': 'pw910hf1',
  // Batch 2: Receivable Detail
  '/receivable-detail': 'pwg103f1',
  // Batch 2: Access Control
  '/access-control': 'pw0107f1',
  // Batch 2: Accounting Voucher
  '/accounting-voucher': 'pw320afx',
  // Batch 2: Consignment Shipment
  '/consignment-ship': 'pwm208f1',
  // Batch 2: Outbound Notice
  '/outbound-notice': 'pwm206f1',
  // Batch 2: New Product Report
  '/new-product-report': 'pwj228f1',
  // Batch 2: Salary Adjustment
  '/salary-adjust': 'pw9234f1',
  // Batch 2: R&D Subcontract
  '/rd-subcontract': 'pwj215f1',
  // Batch 2: Base Salary
  '/base-salary': 'pw9232f1',
  // Batch 2: Invoice Request
  '/invoice-request': 'pwg209f1',
  // Shipping Order
  '/shipping-order': 'pwc203f1',
  // Purchase Order
  '/d201': 'pwd201f1',
  // Card Custody Registry
  '/pw9119': 'pw9119f1',
  // Leave Monthly Closing
  '/pw9614': 'pw9614f1',
  // Warehouse Inventory Report
  '/m302': 'pwm302f1',
};

const routePaths = Object.keys(routeToFormId).sort((a, b) => b.length - a.length);

function getRouteFromPath(path: string): string | null {
  for (const route of routePaths) {
    if (path === route || path.startsWith(`${route}/`)) {
      return route;
    }
  }
  return null;
}

function getFormIdFromPath(path: string): string | null {
  const route = getRouteFromPath(path);
  return route ? routeToFormId[route] : null;
}

function getModuleKeyFromPath(path: string): string | null {
  const route = getRouteFromPath(path);
  return route ? route.replace(/^\//, '') : null;
}

const BugReportButton: React.FC = () => {
  const location = useLocation();
  const [annotating, setAnnotating] = useState(false);
  const [verifyVisible, setVerifyVisible] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [openBugCount, setOpenBugCount] = useState(0);

  const formId = getFormIdFromPath(location.pathname);
  const moduleKey = getModuleKeyFromPath(location.pathname);

  const handleStartAnnotation = useCallback(() => {
    setAnnotating(true);
  }, []);

  const handleCancelAnnotation = useCallback(() => {
    setAnnotating(false);
  }, []);

  const handleSubmitAnnotations = useCallback(async (annotations: Annotation[], screenshots: string[]) => {
    try {
      const user = localStorage.getItem('user') || sessionStorage.getItem('user');
      let reportedBy = 'anonymous';
      if (user) {
        try {
          const parsed = JSON.parse(user);
          reportedBy = parsed.userName || parsed.userId || 'anonymous';
        } catch { /* ignore */ }
      }

      await bugReportApi.create({
        formId: formId || undefined,
        page: location.pathname,
        pageTitle: document.title,
        reportedBy,
        browserInfo: navigator.userAgent,
        annotations,
        screenshots: screenshots.length > 0 ? screenshots : undefined,
      });

      message.success(`已送出 ${annotations.length} 個問題回報`);
      setAnnotating(false);
    } catch (err) {
      message.error('送出失敗，請重試');
      console.error('Bug report submit error:', err);
    }
  }, [formId, location.pathname, moduleKey]);

  const openVerifyModal = useCallback(async () => {
    // Check open bugs before showing modal
    try {
      const result = await bugReportApi.list({ pagePath: location.pathname, size: 100 });
      const openCount = (result.data || []).filter(
        (b) => b.status === 'open' || b.status === 'in_progress'
      ).length;
      setOpenBugCount(openCount);
    } catch {
      setOpenBugCount(0);
    }
    setVerifyVisible(true);
  }, [location.pathname]);

  const handleVerify = useCallback(async () => {
    if (!moduleKey) {
      message.warning('此頁面無對應模組');
      return;
    }

    setVerifyLoading(true);
    try {
      // 1. Close all resolved/open bugs for this page → closed (已驗收)
      try {
        const result = await bugReportApi.list({ pagePath: location.pathname, size: 100 });
        const bugsToClose = (result.data || []).filter((b) => b.status === 'resolved');
        await Promise.all(bugsToClose.map((b) => bugReportApi.update(b.id, { status: 'closed' })));
      } catch (e) { console.error('Failed to close resolved bugs:', e); }

      // 2. Update module status to done
      const updates: Promise<unknown>[] = [
        erpHomeApi.updateStatus(moduleKey, 'verified', 'Accepted by user after bug fix'),
      ];
      if (formId) {
        updates.push(devApi.updateModule(formId, { status: 'done' }));
      }
      await Promise.all(updates);

      // All bugs are now closed → "我的回報" will naturally hide this module
      message.success('驗收完成！模組已標記為完成');
      setVerifyVisible(false);
    } catch (err) {
      message.error('更新失敗，請重試');
      console.error('Verify error:', err);
    } finally {
      setVerifyLoading(false);
    }
  }, [formId, location.pathname, moduleKey]);

  const menuItems = [
    {
      key: 'report',
      label: '回報問題',
      icon: <AlertOutlined />,
      onClick: handleStartAnnotation,
    },
    {
      key: 'verify',
      label: '完成驗收',
      icon: <CheckCircleOutlined />,
      onClick: openVerifyModal,
    },
  ];

  if (annotating) {
    return (
      <BugReportOverlay
        onSubmit={handleSubmitAnnotations}
        onCancel={handleCancelAnnotation}
      />
    );
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        placement="topRight"
        trigger={['click']}
      >
        <Button
          type="primary"
          danger
          shape="circle"
          size="large"
          icon={<BugOutlined />}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1000,
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(255, 77, 79, 0.4)',
          }}
        />
      </Dropdown>

      <VerifyModal
        open={verifyVisible}
        formId={formId}
        pagePath={location.pathname}
        onConfirm={handleVerify}
        onCancel={() => setVerifyVisible(false)}
        loading={verifyLoading}
        openBugCount={openBugCount}
      />
    </>
  );
};

export default BugReportButton;
